<template>
    <input
        type="text"
        class="form-control"
        :style="{ width: inputWidthComputed }"
        :id="id"
        :value="inputValue"
        @keydown="emitKeydown"
        @input="updateValue"
        @change="handleChange"
    />
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import priceParser from '@barchart/marketdata-api-js/lib/utilities/parse/price';
import stringFormatter from '@barchart/marketdata-api-js/lib/utilities/format/specialized/cmdtyView';
import formatPrice from '@barchart/marketdata-api-js/lib/utilities/format/price';
import Profile from '@barchart/marketdata-api-js/lib/marketState/Profile';

const props = defineProps({
    id: String,
    defaultValue: [String, Number],
    inputWidth: String,
    baseCode: String,
    symbolType: Number,
    symbol: String
});

const emit = defineEmits(['update:modelValue', 'keydown']);

const inputValue = ref(props.defaultValue ? formatString(props.defaultValue) : '');
const inputWidthComputed = computed(() => props.inputWidth || '100%');

const valueWithoutComma = computed(() => inputValue.value.replaceAll(',', ''));

function handleChange() {
    if (inputValue.value.indexOf('-') === -1) {
        inputValue.value = formatString(parseFloat(valueWithoutComma.value));
    }

    let decimal;

    // Special case for spreads that can be negative
    if (props.symbolType === 13 && inputValue.value.startsWith('-')) {
        decimal = -1 * formatDecimal(valueWithoutComma.value.slice(1));
    } else {
        decimal = formatDecimal(valueWithoutComma.value);
    }

    if (inputValue.value === '' || Number.isNaN(Number.parseFloat(decimal))) {
        inputValue.value = null;
        return emit('update:modelValue', null);
    }

    emit('update:modelValue', decimal);
}

function updateValue(event) {
    inputValue.value = event.target.value;
}

function formatDecimal(value) {
    const baseCode = props.baseCode || window.Barchart.getBaseCode();
    const isSpecial = props.symbolType === 12;
    const isTreasury = props.symbol.startsWith('ZN') || props.symbol.startsWith('ZF') || props.symbol.startsWith('ZB');

    if (isSpecial && ['6', '5'].includes(baseCode)) {
        const [numerator, denominator] = value.split('-');

        if (denominator) {
            if (denominator.length === 2 && isTreasury === false) {
                value += '0';
            }
        }

        if (numerator && numerator.length === 1) {
            value = `0${value}`;
        }

        if (value.startsWith('-')) {
            value = `0${value}`;
        }

        return priceParser(value, baseCode, '-', isTreasury ? false : true);
    }

    if (value.startsWith('-')) {
        value = `0${value}`;
    }

    return priceParser(value, baseCode, '-', true);
}

function formatString(value) {
    const profile = new Profile(props.symbol, props.symbol, '', props.baseCode, 1000, 1);
    return stringFormatter(value, props.baseCode, profile);
}

function emitKeydown(event) {
    emit('keydown', event);
}

watch(() => props.defaultValue, (newValue) => {
    if (typeof newValue === 'number') {
        inputValue.value = formatString(newValue);
    } else if (typeof newValue === 'string') {
        inputValue.value = newValue;
        handleChange();
    }
});

defineExpose({
    formatString,
    formatDecimal,
});
</script>
